import { Hotkeys } from "../models/hotkeys.model"
import { PlayableMedia } from "../models/resource/media"
import { GTMService } from "../services/gtm.service"
import { GtmVideoJsInfo } from "./types"

export default class PlayerUtils {
    /* General for audio and video
     * "dismiss" key is just a workaround to stop showing the X and Ok buttons. This is a small bug in the errors plugin
     */
    public static ErrorOverrides: any = {
        1: {
            type: "MEDIA_ERR_ABORTED",
            headline:
                "<span class='level1'>The media download was cancelled.</span><span class='level2'>Try refreshing the page and try your download again.</span>",
            dismiss: false,
        },
        2: {
            type: "MEDIA_ERR_NETWORK",
            headline:
                "<span class='level1'>The connection was lost.</span><span class='level2'>Please check your Internet connection and try again.</span>",
            dismiss: false,
        },
        3: {
            type: "MEDIA_ERR_DECODE",
            headline:
                "<span class='level1'>The media appears to be corrupted or in a format that can't be played in your browser.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        4: {
            type: "MEDIA_ERR_SRC_NOT_SUPPORTED",
            headline:
                "<span class='level1'>This media isn't supported in this browser.</span><span class='level3'>For more information, see <a href='https://www.pbslearningmedia.org/help/technical-requirements/#supportedbrowsers'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page.</a></span>",
            dismiss: false,
        },
        5: {
            type: "MEDIA_ERR_ENCRYPTED",
            headline:
                "<span class='level1'>Sorry, we can't play this media.</span><span class='level2'>This media is encrypted and we're unable to decrypt it.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        unknown: {
            type: "MEDIA_ERR_UNKNOWN",
            headline:
                "<span class='level1'>We are experiencing technical difficulties that are preventing us from playing the media at this time. Please check back again soon.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        "-1": {
            type: "PLAYER_ERR_NO_SRC",
            headline:
                "<span class='level1'>For some reason, no media has been loaded.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        "-2": {
            type: "PLAYER_ERR_TIMEOUT",
            headline:
                "<span class='level1'>Sorry, we couldn't download the media you requested.</span><span class='level2'>Please refresh the page and see if it helps.</span>",
            dismiss: false,
        },
        PLAYER_ERR_DOMAIN_RESTRICTED: {
            headline:
                "<span class='level1'>This media is restricted from playing on this website.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        PLAYER_ERR_IP_RESTRICTED: {
            headline:
                "<span class='level1'>This media is restricted from playing at your IP address.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
        PLAYER_ERR_GEO_RESTRICTED: {
            headline:
                "<span class='level1'>This media isn't available in your current location.</span><span class='level2'>This media is only available in the station broadcast coverage area. </span>",
            dismiss: false,
        },
        FLASHLS_ERR_CROSS_DOMAIN: {
            headline:
                "<span class='level1'>This media couldn't be loaded.</span><span class='level2'>The media is not available on this website.</span><span class='level3'>Please help us improve! Visit <a href='https://www.pbslearningmedia.org/help/troubleshooting/#audiovideoerrors'>this article</a> on the <a href='https://www.pbslearningmedia.org/help/'>PBS LearningMedia Help page</a> for information on how to report this problem through email.</span>",
            dismiss: false,
        },
    }

    public static trackCaptionSettingsState(player) {
        let settingsDoneBtn: HTMLButtonElement
        let settingsDefaultBtn: HTMLButtonElement
        let captionSettings: any = {}
        let selectedLanguage: any = {}
        const elementsReady = new Promise((res, rej) => {
            const interval = setInterval(() => {
                settingsDoneBtn = document.querySelector(".vjs-done-button")
                settingsDefaultBtn = document.querySelector(".vjs-default-button")

                if (settingsDoneBtn) {
                    clearInterval(interval)
                    res(true)
                }
            }, 250)
        })

        elementsReady.then(() => {
            PlayerUtils.loadCaptionSettings(player)
            settingsDoneBtn.addEventListener("click", () => {
                captionSettings = player.textTrackSettings.getValues()
                PlayerUtils.saveCaptionSettings(captionSettings)
            })
            settingsDefaultBtn.addEventListener("click", () => {
                captionSettings = player.textTrackSettings.setDefaults()
                captionSettings = player.textTrackSettings.getValues()
                PlayerUtils.saveCaptionSettings(captionSettings)
            })
        })

        player.textTracks().on("change", () => {
            const playerCache = player.getCache()
            selectedLanguage = playerCache.selectedLanguage
            PlayerUtils.saveCaptionLanguage(selectedLanguage)
            PlayerUtils.setCaptionFlag(player, selectedLanguage)
        })
    }

    public static saveCaptionSettings(obj) {
        localStorage.setItem("videojs-caption-settings", JSON.stringify(obj))
    }

    public static saveCaptionLanguage(obj) {
        localStorage.setItem("videojs-caption-language", JSON.stringify(obj))
    }

    public static loadCaptionSettings(player) {
        const captionSettings: any = localStorage.getItem("videojs-caption-settings")
        if (captionSettings) {
            player.textTrackSettings.setDefaults()
            player.textTrackSettings.setValues(JSON.parse(captionSettings))
            player.textTrackSettings.updateDisplay()
        }
    }

    public static setCaptionFlag(player, selectedLanguage) {
        if (selectedLanguage && selectedLanguage.enabled) {
            player.removeClass("captions-off")
        } else {
            player.addClass("captions-off")
        }
    }

    public static loadCaptionLanguage(player, media: PlayableMedia) {
        let selectedLanguage: any = localStorage.getItem("videojs-caption-language")
        const tracks = media.getVideoJsTracks()
        if (selectedLanguage && selectedLanguage !== "undefined") {
            // !== "undefined" is for safari
            selectedLanguage = JSON.parse(selectedLanguage)
            if (!selectedLanguage || !selectedLanguage.enabled) {
                tracks.forEach((track) => {
                    track.default = false
                })
            } else {
                const languageFound = tracks.find(
                    (track) => track.srclang === selectedLanguage.language
                )

                if (languageFound) {
                    tracks.forEach((track) => {
                        track.default = false
                    })
                    languageFound.default = true
                }
                // Otherwise, need to keep default primary caption selection
            }
            PlayerUtils.setCaptionFlag(player, selectedLanguage)
        }
        tracks.forEach((track) => player.addRemoteTextTrack(track, false))
    }

    /*
    Complete common setup for players.

    Call only once after a player is created.
    Do not call after source changes as this is not idempotent.

    We need to pass the hotKeys settings here because the original settings that were set in the audio/video
    components were overwritten by the ones set here.
     */
    public static finalizePlayerSetup(
        player,
        gtmService: GTMService,
        gtmVideoJsInfo: GtmVideoJsInfo,
        hotKeysSettings
    ) {
        player.ready(() => {
            let timer = null
            let totalTime = 0
            const hotKeysSetup = {
                rewindKey(e) {
                    if (e.which === Hotkeys.left || e.which === Hotkeys.previous) {
                        gtmService.pushEvent("VideoRewindBack")
                        return true
                    }
                },
                volumeUpKey(e) {
                    if (e.which === Hotkeys.up) {
                        gtmService.pushEvent("VideoVolumeChangeEvent")
                        return true
                    }
                },
                volumeDownKey(e) {
                    if (e.which === Hotkeys.down) {
                        gtmService.pushEvent("VideoVolumeChangeEvent")
                        return true
                    }
                },
                muteKey(e) {
                    if (e.which === Hotkeys.m) {
                        gtmService.pushEvent("VideoMuteEvent")
                        return true
                    }
                },
                fullscreenKey(e) {
                    if (e.which === Hotkeys.f) {
                        gtmService.pushEvent("videoFullscreenHotkey")
                        return true
                    }
                },
            }

            player.on("play", () => {
                gtmService.pushEvent("VideoPlayEvent")
                timer = window.setInterval(() => {
                    totalTime += 300
                }, 300)
            })

            player.on("pause", () => {
                if (timer) {
                    clearInterval(timer)
                }
                gtmService.setVideoTimePlayed(Math.round(totalTime / 1000))
                gtmService.pushEvent("VideoPauseEvent")
            })

            player.on("ended", () => {
                gtmService.pushEvent("VideoEnd")
            })

            // capture keys events and send to gtm
            if (player.hotkeys) {
                player.hotkeys({ ...hotKeysSettings, ...hotKeysSetup })
            }
            gtmService.setMediaType(gtmVideoJsInfo.type)
            gtmService.setVideoInfo(`${gtmVideoJsInfo.title} | ${gtmVideoJsInfo.guid}`)
            player.addClass("selenium-player-ready")
        })
    }
}
